/**
 * A "multi-block" manipulates multiple query blocks together, for instance
 * multiple filters.
 */
import {Component} from 'panel';

import {filterAttributes} from 'common/util';
import {removeByValue, replaceByIndex, moveByIndex} from 'common/util/array';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {registerMPElement} from 'common/util/register-element';
import {toSentenceCase} from 'common/util/string';
import QueryBlock from 'common/widgets/query-block';
import {THEME, SELECTOR_TYPE} from 'common/widgets/query-block/constants';
import {getSegmentType} from 'common/widgets/query-block/segment-menu/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';

// @ts-ignore
import template from './index.jade';

// @ts-ignore
import css from './index.styl?inline';
import {preventTracking} from 'common/util/dom';

interface QueryMultiBlockState {
  addingBlock: boolean;
  prefixRenderer: any;
  suffixRenderer: any;
  // this has the same effect as this.state.selectionObject in query-builder. Read there for context
  stagingSelected: Array<Record<string, any>>;
}

class QueryMultiBlock extends Component<QueryMultiBlockState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'adding-disabled': `boolean`,
      'allow-cohort-filter': `boolean`,
      'all-data-groups': `boolean`,
      'block-prefix': `string`,
      'button-icon': {
        type: `string`,
        default: `plus`,
      },
      'button-input-only': {
        type: `boolean`,
        default: true,
      },
      'button-open': `boolean`,
      'button-text': {type: `string`, default: null},
      'button-selector-type': {type: `string`, default: SELECTOR_TYPE.BUTTON, enum: Object.values(SELECTOR_TYPE)},
      'button-selector-theme': {type: `string`, default: THEME.SECONDARY, enum: Object.values(THEME)},
      'button-is-removable': `boolean`,
      'data-group-id': `string`,
      'event-names': {type: `json`, default: null},
      'enable-behavioral-properties': {type: `boolean`, default: false},
      'exclude-close-selectors': {type: `json`, default: []},
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `all`],
        default: `none`,
      },
      'first-block-prefix': `string`,
      layout: {type: `string`, enum: [`row`, `column`], default: `row`},
      limit: {type: `number`, default: null},
      'other-group-by-options': {type: `string`, default: `[]`},
      'property-combine-mode': {type: `string`, default: null},
      'project-has-cohorts': `boolean`,
      'project-has-events': {type: `boolean`, default: true},
      'project-has-people': `boolean`,
      'project-id': {type: `number`, default: null},
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-special-date-property': `boolean`,
      'show-special-date-property-unit': `boolean`,
      selected: {type: `json`, default: []},
      'style-override': {
        type: `string`,
        default: ``,
        description: `used to support legacy design patterns in funnels. do not rely on this, it will be removed soon.`,
      },
      'selector-type': QueryBlock.attrsSchema[`selector-type`],
      theme: {type: `string`, default: THEME.PRIMARY, enum: Object.values(THEME)},
      type: {type: `string`, default: null},
      'user-id': `number`,
      'support-b2b-cohorts': `boolean`,
      'support-inline-cohorts': `boolean`,
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-query-builder] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
      },
      'has-reorder-controls': `boolean`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        addingBlock: false,
        prefixRenderer: null,
        suffixRenderer: null,
        stagingSelected: [],
      },

      helpers: {
        preventTracking,
        propagateEvent: (ev) => propagateEvent(this.app, ev),
        buttonText: () => this.attr(`button-text`) || toSentenceCase(this.attr(`type`)),
        canAddMore: () => !Number.isInteger(this.attr(`limit`)) || this.attr(`limit`) > this.attr(`selected`).length,
        removeSelection: (selectionValue: object) => this.updateItems(`remove`, {selectionValue}),
        setAddingBlock: (addingBlock) => this.update({addingBlock}),
        onAddItemBlockClose: () => {
          this.helpers.setAddingBlock(false);
          this.dispatchEvent(new CustomEvent(`close`));
        },
        moveSelection: (index: number, newIndex: number) =>
          this.updateItems(`reorder`, {
            index,
            newIndex,
          }),

        modifySelection: (index, ev) => {
          const {trackingProps} = ev.detail;
          const item = this.extractItemFromEvent(ev);
          this.updateItems(`edit`, {item, index, trackingProps});
        },

        selectionIsCohort: (selection) => getSegmentType(selection) === ResourceType.Cohort,

        commitNewBlock: (ev) => {
          const {trackingProps} = ev.detail;
          const item = this.extractItemFromEvent(ev);

          this.helpers.setAddingBlock(false);
          this.updateItems(`add`, {item, trackingProps});
        },

        getQueryBlockAttrs: (attrs) => {
          return filterAttributes({
            type: this.attr(`type`),
            'allow-cohort-filter': this.attr(`allow-cohort-filter`),
            'all-data-groups': this.attr(`all-data-groups`),
            'data-group-id': this.attr(`data-group-id`),
            'enable-behavioral-properties': this.attr(`enable-behavioral-properties`),
            'event-names': JSON.stringify(this.attr(`event-names`)),
            'exclude-close-selectors': JSON.stringify(this.attr(`exclude-close-selectors`)),
            'include-custom-properties': this.attr(`include-custom-properties`),
            'header-text': ``,
            'other-group-by-options': this.attr(`other-group-by-options`),
            'property-combine-mode': this.attr(`property-combine-mode`),
            'project-has-cohorts': this.attr(`project-has-cohorts`),
            'project-has-events': this.attr(`project-has-events`),
            'project-has-people': this.attr(`project-has-people`),
            'hide-edit-controls': this.attr(`hide-edit-controls`),
            'show-special-date-property': this.attr(`show-special-date-property`),
            'project-id': this.attr(`project-id`),
            'style-override': this.attr(`style-override`),
            'selector-type': this.attr(`selector-type`),
            theme: this.attr(`theme`),
            'user-id': this.attr(`user-id`),
            'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
            'support-inline-cohorts': this.attr(`support-inline-cohorts`),
            'track-name': this.attr(`track-name`),
            'workspace-id': this.attr(`workspace-id`),
            ...attrs,
          });
        },
      },
    };
  }

  set prefixRenderer(prefixRenderer) {
    this.update({prefixRenderer});
  }

  set suffixRenderer(suffixRenderer) {
    this.update({suffixRenderer});
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected`) {
      this.update({
        stagingSelected: this.attr(`selected`),
      });
    }
  }

  private extractItemFromEvent(ev) {
    switch (this.attr(`type`)) {
      case `cohorts`:
        return ev.detail.selected;
      default:
        return ev.detail.item;
    }
  }

  private updateItems(
    action,
    {
      item,
      index,
      newIndex,
      selectionValue,
      trackingProps = {},
    }: {
      item?: any;
      index?: number;
      newIndex?: number;
      selectionValue?: object;
      trackingProps?: object;
    },
  ) {
    let additionalTrackingProps = {};
    let selections = this.state.stagingSelected || [];
    switch (action) {
      case `add`:
        selections = [...selections, item];
        break;
      case `edit`:
        selections = replaceByIndex(selections, index, item);
        break;
      case `reorder`:
        selections = moveByIndex(selections, index, newIndex);
        // Applied dynamically after the action is performed
        additionalTrackingProps = {
          ...additionalTrackingProps,
          'current position': index,
          'new position': newIndex,
          direction: newIndex > index ? `right` : `left`,
          'Breakdown List': selections
            .filter((selection) => !this.helpers.selectionIsCohort(selection))
            .map((selection) => selection[0] && selection[0].event && selection[0].event.propertyName),
        };
        break;
      case `remove`:
        item = selectionValue;
        // note we can't remove by index here. This is because within a frame, due to callbacks, all
        // selections in the array may need to remove themselves. For a 2-array, 'remove 0' and
        // 'remove 1' from children would have 'remove 1' fail due to us having a proper view
        // here due to the buffered selection object but the children underneath having an outdated
        // one as the drawing cycle has not completed yet.
        selections = removeByValue(selections, selectionValue);
        break;
    }
    const {eventName, props} = this.trackingProps(action, item, {
      ...additionalTrackingProps,
      ...trackingProps,
    });
    this.update({stagingSelected: selections});
    this.dispatchEvent(new CustomEvent(`update`, {detail: {items: selections}}));
    emitTrackEvent(this, {eventName, props, eventPrefix: null, cancelable: true});
  }

  // Generates tracking properties for a given configuration of the component
  private trackingProps(action, item, trackingProps) {
    const menuType = this.attr(`type`);
    let eventName = ``;
    let props = trackingProps || {};

    // TODO delegate this to the child somehow
    switch (menuType) {
      case `breakdown`:
        eventName = `${toSentenceCase(action)} event/property breakdown`;
        props = {
          'Event name': item.event.label,
          'Property data type': item.property.type,
          'Property name': item.property.label,
          'Property raw name': item.property.name,
        };
        break;
      case `properties`:
        eventName = `${toSentenceCase(action)} property`;
        props = {
          'Property data type': item.type,
          'Property name': item.label,
          'Property raw name': item.name,
        };
        break;
      case `filter`:
        eventName = `${toSentenceCase(action)} property filter`;
        break;
      case `segment`:
        eventName = `${toSentenceCase(action)} segment`;
        break;
      default:
        throw new Error(`tracking not defined for mp-query-multi-block configuration`);
    }

    return {eventName, props};
  }
}

export default registerMPElement(`mp-query-multi-block`, QueryMultiBlock);
